/**
 * Known MCP clients, identified by REDIRECT ORIGIN.
 *
 * Client registration is open to anyone (deliberately — a connector holds nothing but the URL
 * when it registers, and that is what makes "paste the URL and authorize" work). The cost is
 * that `client_name` is self-asserted: a stranger can register as "Claude", point
 * `redirect_uri` at their own server, and send a shop owner a valid authorization URL.
 *
 * ⭐ THE FIX IS TO STOP READING THE NAME, NOT TO POLICE IT. This module derives the displayed
 * identity from the redirect ORIGIN, which is the one field on the consent screen the attacker
 * cannot obtain: Better Auth validates `redirect_uri` against the client's registered set
 * before the consent screen renders, so the origin shown is genuinely where the code will go.
 * Nothing here ever reads `client_name`, so there is no string for an attacker to craft.
 *
 * 🔴 Blocking names at registration was considered and rejected. It is whack-a-mole — "Claude
 * Assistant", "Cl aude", a homoglyph — and it falsely rejects a legitimate third party whose
 * product genuinely shares a word. A control that can be renamed around is not a control.
 *
 * ⚠️ ORIGIN, NOT FULL URI, AND THIS IS LOAD-BEARING. ChatGPT alone is documented with at least
 * four callback paths (`/connector_platform_oauth_redirect`, `/connector/oauth/{callback_id}`,
 * `/oauth/callback`, and the legacy `chat.openai.com` host), and which one it uses depends on
 * whether the authorization server meets its issuer-identification requirements. An exact-URI
 * allowlist would silently stop badging the real ChatGPT the first time it moved a path. The
 * path carries no security — reaching ANY path on `chatgpt.com` is the thing an attacker
 * cannot do — so the origin is both the safe and the durable thing to pin.
 */

/** A client we can name with confidence, because only they control that origin. */
export type KnownClient = {
  /** What the consent screen shows. OURS, never the client's self-asserted string. */
  readonly name: string;
  /** Exact origins (scheme + host + port), compared against `new URL(uri).origin`. */
  readonly origins: readonly string[];
};

/**
 * ⚠️ HTTPS ONLY, AND NO LOOPBACK ENTRIES — both deliberate.
 *
 * Claude Code authorizes over a loopback redirect (`http://localhost:<port>/callback`, the port
 * varying per run). That address proves nothing about who is listening: any process on the
 * owner's machine can bind it, so badging it would hand the badge to whatever asked for it.
 * Claude Code therefore renders UNVERIFIED, which is the honest answer rather than a gap —
 * it is the one case where the owner genuinely started the flow themselves and can tell.
 */
export const KNOWN_CLIENTS: readonly KnownClient[] = [
  {
    name: 'Claude',
    origins: ['https://claude.ai'],
  },
  {
    name: 'ChatGPT',
    origins: ['https://chatgpt.com', 'https://chat.openai.com'],
  },
] as const;

/** What the consent screen may say about who is asking. */
export type ClientIdentity =
  | { readonly verified: true; readonly name: string }
  | { readonly verified: false };

const UNVERIFIED: ClientIdentity = { verified: false };

/**
 * Identify a client by its redirect URI, or decline to.
 *
 * 🔴 FAILS TOWARD UNVERIFIED, ALWAYS. Every unparseable, unknown, non-https or loopback URI
 * returns `{verified:false}`, and the screen then shows what it shows today — the self-asserted
 * name, labelled as unverified, beside the real host. A stale allowlist costs a missing badge
 * on a real client; the opposite default would put a badge on an impostor.
 *
 * ⭐ Exact-origin comparison via `URL.origin` is what makes the lookalikes fail, and each one
 * fails for a different reason worth naming:
 *   · `claude.ai.evil.test` — a DIFFERENT host. Only `===` sees that; `endsWith` does not.
 *   · `evil.test/claude.ai` — origin ignores the path entirely.
 *   · `xn--clude-9ta.ai` (homograph) — `URL` normalises IDN to punycode in `host`, so the
 *     lookalike arrives ASCII-different from `claude.ai` and cannot match.
 *   · `https://user@claude.ai` — `origin` drops userinfo, so credentials cannot smuggle a
 *     different authority past a naive string compare of the whole URL.
 *   · `http://claude.ai` — a different origin from the https one, and refused: a downgraded
 *     scheme means the code can be read in transit.
 */
export function identifyClient(redirectUri: string | null | undefined): ClientIdentity {
  if (!redirectUri) return UNVERIFIED;

  let origin: string;
  try {
    const url = new URL(redirectUri);
    /**
     * ⚠️ THIS CHECK IS REDUNDANT TODAY, AND IS KEPT DELIBERATELY — said plainly because a
     * mutation run proved it. Removing it kills nothing: `URL.origin` carries the scheme, so
     * `http://claude.ai` is already a different string from `https://claude.ai` and fails the
     * exact match below on its own. (Opaque schemes give the literal string 'null', which
     * likewise matches nothing.)
     *
     * It stays as the second lock on ONE mistake the exact match cannot catch: an `http://`
     * entry added to KNOWN_CLIENTS later. `known-clients.test.ts` asserts the same property
     * over the list, so that error now has to get past both.
     */
    if (url.protocol !== 'https:') return UNVERIFIED;
    origin = url.origin;
  } catch {
    return UNVERIFIED;
  }

  for (const client of KNOWN_CLIENTS) {
    if (client.origins.includes(origin)) {
      return { verified: true, name: client.name };
    }
  }
  return UNVERIFIED;
}
